/**
 * Merge fields a CRM message template / composer draft may use. Mirrors the
 * backend (services/crmTemplate.js MERGE_FIELDS + isKnownMergeField) so the UI's
 * insert helper and "unknown token" warning match what the server accepts at
 * save and renders at send. Keep the two in lock-step.
 */
export interface MergeFieldDef {
  /** The bare token path, e.g. 'first_name'. */
  token: string;
  /** Recruiter-facing label for the insert chip. */
  label: string;
}

export const MERGE_FIELDS: MergeFieldDef[] = [
  { token: 'first_name', label: 'First name' },
  { token: 'last_name', label: 'Last name' },
  { token: 'full_name', label: 'Full name' },
  { token: 'study_title', label: 'Study name' },
  { token: 'study_slug', label: 'Study slug' },
];

const SCALARS = new Set(MERGE_FIELDS.map((f) => f.token));

/** A token path is known if it's a scalar field or an answer.<question_id> reference. */
export function isKnownMergeField(path: string): boolean {
  const p = (path || '').trim();
  return SCALARS.has(p) || /^answer\.[\w.]+$/.test(p);
}

/** Unique UNKNOWN {{token}}s in a string — token-shaped mistypes like {{study}}. */
export function findUnknownMergeFields(text: string): string[] {
  if (!text) return [];
  const bad = new Set<string>();
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (!isKnownMergeField(m[1])) bad.add(m[1].trim());
  }
  return [...bad];
}

/**
 * Insert `token` at the cursor of a textarea/input, updating React state via
 * setValue and restoring focus + caret after the token. Falls back to appending
 * when the element/selection is unavailable.
 */
export function insertAtCursor(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  current: string,
  setValue: (v: string) => void,
  token: string,
): void {
  if (!el || el.selectionStart == null) {
    setValue(`${current}${token}`);
    return;
  }
  const start = el.selectionStart;
  const end = el.selectionEnd ?? start;
  setValue(current.slice(0, start) + token + current.slice(end));
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + token.length;
    el.setSelectionRange(pos, pos);
  });
}
